import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, downloadCsv, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import ProgressBar from "@/components/common/ProgressBar";
import { MagnifyingGlass, Funnel, ArrowRight, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Participants() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [risk, setRisk] = useState("");
  const [funded, setFunded] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (risk) params.risk_status = risk;
      if (funded) params.funded = funded;
      const { data } = await api.get("/participants", { params });
      setRows(data);
    } catch (e) { toast.error("Failed to load"); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, risk, funded]);

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <PageHeader
        title="Participants"
        subtitle="Approved traders, watchlist, and disqualified accounts."
        breadcrumbs="Participants"
        testId="participants-page"
        actions={
          <button
            data-testid="participants-export-btn"
            onClick={async () => {
              try { await downloadCsv("/exports/participants.csv"); toast.success("Participants exported"); }
              catch (e) { toast.error(formatApiError(e)); }
            }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/10 text-gray-200 hover:bg-white/5 text-sm"
          >
            <DownloadSimple size={14} /> Export CSV
          </button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            data-testid="participants-search"
            placeholder="Search name, account, email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="w-full bg-[#111827] border border-white/10 rounded-md pl-9 pr-4 py-2 text-sm text-white outline-none focus:border-blue-500"
          />
        </div>
        <select data-testid="participants-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="bg-[#111827] border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none">
          <option value="">All status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="active">Active</option>
          <option value="rejected">Rejected</option>
          <option value="disqualified">Disqualified</option>
          <option value="bali_qualified">Bali Qualified</option>
          <option value="pending_bali_audit">Pending Bali Audit</option>
        </select>
        <select data-testid="participants-filter-risk" value={risk} onChange={(e) => setRisk(e.target.value)} className="bg-[#111827] border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none">
          <option value="">All risk</option>
          <option value="healthy">Healthy</option>
          <option value="watchlist">Watchlist</option>
          <option value="high_risk">High Risk</option>
          <option value="suspicious">Suspicious</option>
        </select>
        <select data-testid="participants-filter-funded" value={funded} onChange={(e) => setFunded(e.target.value)} className="bg-[#111827] border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none">
          <option value="">All balances</option>
          <option value="true">Funded only</option>
          <option value="false">Not funded</option>
        </select>
        <button onClick={load} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md">Search</button>
      </div>

      <div className="bg-[#111827] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#1F2937]/50 text-left text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3">Name / Account</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Risk</th>
                <th className="px-4 py-3 text-right">Valid Lot</th>
                <th className="px-4 py-3">Progress to 30</th>
                <th className="px-4 py-3">Milestone</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="p-8 text-center text-gray-500">Loading…</td></tr> :
                rows.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-gray-500">No participants</td></tr> :
                rows.map((p) => (
                  <tr key={p.id} data-testid={`participant-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{p.full_name}</div>
                      <div className="text-xs text-gray-500 font-mono">{p.broker_account_number} · {p.broker_server}</div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3"><StatusBadge status={p.risk_status || "healthy"} /></td>
                    <td className="px-4 py-3 text-right font-mono text-white">{(p.total_valid_lot || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 min-w-[200px]">
                      <ProgressBar value={p.total_valid_lot || 0} />
                    </td>
                    <td className="px-4 py-3 font-mono text-orange-300">{p.current_milestone || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/participants/${p.id}`} data-testid={`participant-view-${p.id}`} className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-xs">
                        View <ArrowRight size={12} />
                      </Link>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
